import * as React from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';

import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined';
import LocalCafeOutlinedIcon from '@mui/icons-material/LocalCafeOutlined';
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import PlumbingOutlinedIcon from '@mui/icons-material/PlumbingOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import DirectionsCarFilledOutlinedIcon from '@mui/icons-material/DirectionsCarFilledOutlined';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';

import { Navigate, useLocation } from 'react-router-dom';

import AppConfig from '../services/app-config';
import AuthService from '../services/auth';

export default function Servicios() {
    const location = useLocation();
    const [redirect, setRedirect] = React.useState({ trigger: false, route: '/' });

    const handleClick = React.useCallback((route) => {
        setRedirect({ trigger: true, route: `/${route.toLowerCase()}` });
    }, []);

    React.useEffect(() => {
        if (!AuthService.isLogged()) {
            setRedirect({ trigger: true, route: '/login' });
            return;
        }

        if (location.pathname !== '/servicios') {
            return;
        }
    }, [location.pathname]);

    const services = React.useMemo(() => {
        const items = [];

        if (AppConfig.haveModule('visits')) {
            items.push({
                id: 'visitas',
                title: 'Visitas',
                subtitle: 'Gestiona tus visitas',
                route: 'visitas',
                icon: <PersonAddAltOutlinedIcon sx={{ color: '#238048', fontSize: 28 }} />,
            });
        }

        if (AppConfig.haveModule('amenities')) {
            items.push({
                id: 'amenidades',
                title: 'Amenidades',
                subtitle: 'Reserva y explora las amenidades',
                route: 'amenidades',
                icon: <LocalCafeOutlinedIcon sx={{ color: '#238048', fontSize: 28 }} />,
            });
        }

        if (AppConfig.haveModule('airbnb') && AppConfig.get('unit_mode') === 'airbnb') {
            items.push({
                id: 'airbnb',
                title: 'Airbnb',
                subtitle: 'Gestiona las reservaciones de tu Airbnb',
                route: 'airbnb',
                icon: <ConfirmationNumberOutlinedIcon sx={{ color: '#238048', fontSize: 28 }} />,
            });
        }

        if (AppConfig.haveModule('issues')) {
            items.push({
                id: 'reportes',
                title: 'Averías',
                subtitle: 'Reporta o revisa averías',
                route: 'reportes',
                icon: <PlumbingOutlinedIcon sx={{ color: '#238048', fontSize: 28 }} />,
            });
        }

        if (AppConfig.get('appear_in_directory') && AppConfig.haveModule('directory')) {
            items.push({
                id: 'directorio',
                title: 'Directorio',
                subtitle: 'Busca a tus vecinos',
                route: 'directorio',
                icon: <LocationOnOutlinedIcon sx={{ color: '#238048', fontSize: 28 }} />,
            });
        }

        if (AppConfig.haveModule('parksharing')) {
            items.push({
                id: 'parqueos',
                title: 'Estacionamiento',
                subtitle: 'Parqueos compartidos',
                route: 'parqueos',
                icon: <DirectionsCarFilledOutlinedIcon sx={{ color: '#238048', fontSize: 28 }} />,
            });
        }

        if (AuthService.userRoleIncludes(['admin'])) {
            const orgFriendlyName = AppConfig.get('friendly_name') || AuthService.getCurrentUserOrg() || 'Organizacion';
            items.unshift({
                id: 'admin-dashboard',
                title: `Configuraciones de ${orgFriendlyName}`,
                subtitle: 'Configura modulos, notificaciones y reglas',
                route: 'admin/dashboard',
                icon: <DashboardOutlinedIcon sx={{ color: '#238048', fontSize: 28 }} />,
                tourId: 'services-dashboard-card',
            });
        }

        return items;
    }, []);

    if (redirect.trigger) {
        return <Navigate to={redirect.route} replace={true} />;
    }

    return (
        <Box sx={{ px: 3, pt: 2, pb: 12, bgcolor: 'background.default', minHeight: '100vh' }} data-tour="services-root">
            <Typography sx={{ fontSize: 18, fontWeight: 900, color: 'text.primary', mb: 2 }}>
                Servicios
            </Typography>

            <Grid container spacing={2}>
                {services.map((item) => (
                    <Grid item xs={12} key={item.id} data-tour={item.tourId}>
                        <Card
                            onClick={() => handleClick(item.route)}
                            sx={{
                                borderRadius: '10px',
                                boxShadow: 0,
                                border: '1px solid',
                                borderColor: 'divider',
                                cursor: 'pointer',
                            }}
                        >
                            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
                                        <Box
                                            sx={{
                                                width: 48,
                                                height: 48,
                                                borderRadius: '999px',
                                                backgroundColor: 'rgba(35,128,72,0.10)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                flexShrink: 0,
                                            }}
                                        >
                                            {item.icon}
                                        </Box>
                                        <Box sx={{ minWidth: 0 }}>
                                            <Typography sx={{ fontSize: 14, fontWeight: 900, color: 'text.primary' }}>
                                                {item.title}
                                            </Typography>
                                            <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary' }}>
                                                {item.subtitle}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>
        </Box>
    );
}
